import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Lock, LockOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";

const fmt = (s) => { try { return new Date(s).toLocaleString("sk-SK", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return s; } };

export const LockBadge = ({ unlocked, testid }) => unlocked
  ? <span className="rounded bg-orange-500/20 px-2 py-0.5 text-[10px] font-bold uppercase text-orange-300" data-testid={testid}>Odomknutá</span>
  : <span className="rounded bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-300" data-testid={testid}>Uzamknutá</span>;

export function InventoryLockPanel({ rec, onChange }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [mode, setMode] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/warehouse/inventories/${rec.id}/${mode}`, mode === "unlock" ? { reason: text } : { note: text });
      toast.success(mode === "unlock" ? "Mesiac odomknutý" : "Mesiac znova uzamknutý");
      setMode(null); setText(""); onChange(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const log = rec.lock_log || [];
  return (
    <div className={`rounded-lg border p-3 text-sm ${rec.unlocked ? "border-orange-500/40 bg-orange-500/10" : "border-border bg-card/60"}`} data-testid="inventory-lock-panel">
      <div className="flex flex-wrap items-center gap-3">
        <LockBadge unlocked={rec.unlocked} testid="inventory-lock-status" />
        <span className="text-muted-foreground">{rec.unlocked ? "V tomto mesiaci sa dajú robiť príjmy, faktúry a doklady. Nezabudnite ho znova zamknúť." : "Mesiac je chránený proti spätným zmenám."}</span>
        {isAdmin && (rec.unlocked
          ? <Button size="sm" className="ml-auto gap-2" onClick={() => { setText(""); setMode("lock"); }} data-testid="inventory-lock-btn"><Lock className="h-4 w-4" /> Znova zamknúť</Button>
          : <Button size="sm" variant="outline" className="ml-auto gap-2" onClick={() => { setText(""); setMode("unlock"); }} data-testid="inventory-unlock-btn"><LockOpen className="h-4 w-4" /> Odomknúť mesiac</Button>)}
      </div>
      {log.length > 0 && (
        <div className="mt-3 space-y-1 border-t border-border/50 pt-2" data-testid="inventory-lock-log">
          {log.map((e, i) => (
            <div key={i} className="text-xs text-muted-foreground" data-testid={`inventory-lock-log-${i}`}>
              <b className={e.action === "unlock" ? "text-orange-300" : "text-emerald-300"}>{e.action === "unlock" ? "Odomknutie" : "Zamknutie"}</b> · {fmt(e.at)} · {e.by}{e.text ? ` · ${e.text}` : ""}
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!mode} onOpenChange={(o) => { if (!o) { setMode(null); } }}>
        <DialogContent className="sm:max-w-md" data-testid="inventory-lock-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase">{mode === "unlock" ? `Odomknúť ${rec.month}?` : `Znova zamknúť ${rec.month}?`}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{mode === "unlock" ? "Inventúra ostane bez zmeny. V mesiaci sa znova povolia príjmy do tohto skladu; faktúry a doklady až keď sú odomknuté všetky inventúry mesiaca." : "Mesiac bude znova chránený proti spätným zmenám."}</p>
          <div className="space-y-1.5">
            <Label>{mode === "unlock" ? "Dôvod (povinný)" : "Poznámka (voliteľná)"}</Label>
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={mode === "unlock" ? "napr. oprava faktúry č. …" : ""} data-testid="inventory-lock-text" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMode(null)}>Zrušiť</Button>
            <Button onClick={submit} disabled={busy || (mode === "unlock" && !text.trim())} data-testid="inventory-lock-confirm">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{mode === "unlock" ? "Odomknúť" : "Zamknúť"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
